import { Component, OnInit, ViewChild } from '@angular/core';
import { OnyxEvent } from '../models/event';
import { FormGroup, FormBuilder } from '@angular/forms';
import { EventStore } from '../service/event.store';
import { Observable } from 'rxjs/Observable';
import { Router, ActivatedRoute } from '@angular/router';
import { MenuStore } from '../../menus/service/menu.store';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { Party } from '../models/party';
import { Contact } from '../models/contact';
import { PartyType } from '../models/party-type';
import { isDefined } from '@ngx-translate/core/src/util';
import { RouteUtils } from '../utils/route-utils';
import { EventUtils } from '../utils/event-utils';
import { ConfirmationModalComponent } from '../../shared/confirmation-modal/confirmation-modal.component';
import { ConfirmationModalConfig } from '../../shared/confirmation-modal/confirmation-modal-config';
import { TranslateService } from '@ngx-translate/core';
import { EventSearchParametersStore } from '../service/event-search-parameters.store';
import { AuthenticationStore } from '../../permission/services/authentication.store';

@Component({
  selector: 'pioneer-event',
  templateUrl: './event.component.html'
})
export class EventComponent implements OnInit {
  public eventForm: FormGroup;

  public onyxEvent: OnyxEvent;

  public isReadOnly = true;

  @ViewChild(ConfirmationModalComponent)
  confirmationModal: ConfirmationModalComponent;

  confirmationModalConfig = new ConfirmationModalConfig();

  constructor(public router: Router,
              private eventStore: EventStore,
              private formBuilder: FormBuilder,
              private menuStore: MenuStore,
              private flashMessagesService: FlashMessagesService,
              private translateService: TranslateService,
              private route: ActivatedRoute,
              private authenticationSore: AuthenticationStore,
              private eventSearchParametersStore: EventSearchParametersStore) {
  }

  ngOnInit() {
    this.menuStore.setPageTitle('EVENT.EVENT_INFO_TITLE');
    this.menuStore.setIsReadOnly(true);

    this.menuStore.isReadOnly.subscribe(isReadOnly => {
      this.isReadOnly = isReadOnly;
    });

    this.onyxEvent = new OnyxEvent();
    this.eventStore.selectedEvent.subscribe(value => {
      this.onyxEvent = new OnyxEvent(value);
    });

    this.eventForm = this.formBuilder.group({
      'event': this.formBuilder.group({
        'eventAgency': this.formBuilder.group({}),
        'eventClient': this.formBuilder.group({}),
        'eventProperty': this.formBuilder.group({})
      })
    });

    // toggle page to editable if the URL ends with "edit" (it will default to read only)
    // otherwise, check if we need to show a warning flash message when in view modeFD
    RouteUtils.getLastUrlSegment(this.route)
      .subscribe(lastSegment => {
        if (lastSegment && lastSegment.path === 'edit') {
          this.togglePageToReadOnly(false);
        } else {
          if (this.authenticationSore.hasPermission('EditEvent') && !this.isEventValid()) {
            this.flashMessagesService.warn('EVENT.EVENT_MISSING_INFO',
              null,
              { hrefValue: `/#/event/${this.onyxEvent.eventId}/edit` });
          }
        }
      });
  }

  save(model: any): Observable<OnyxEvent> {

    model.event.eventAgency = model.eventAgency;
    model.event.eventClient = model.eventClient;
    model.event.eventProperty = model.eventProperty;

    let formModel = Object.assign({}, this.onyxEvent.toJS(), model.event);
    const updatedFields: Object = {};

    if (formModel.eventAgency) {
      const eventAgency = this.onyxEvent.eventAgency || new Party({ contact: new Contact() });
      updatedFields[ 'agencyId' ] = formModel.eventAgency.agencyId;
      updatedFields[ 'eventAgency' ] = Object.assign({}, eventAgency, model[ 'eventAgency' ]);
      updatedFields[ 'eventAgency' ][ 'contact' ] = Object.assign({}, eventAgency.contact, model[ 'eventAgency' ][ 'contact' ]);
    }

    if (formModel.eventClient) {
      const eventClient = this.onyxEvent.eventClient || new Party({ contact: new Contact() });
      updatedFields[ 'clientReferenceNumber' ] = formModel.eventClient.clientReferenceNumber;
      updatedFields[ 'eventClient' ] = Object.assign({}, eventClient, model[ 'eventClient' ]);
      updatedFields[ 'eventClient' ][ 'contact' ] = Object.assign({}, eventClient.contact, model[ 'eventClient' ][ 'contact' ]);
    }

    if (formModel.eventProperty) {
      const eventProperty = this.onyxEvent.eventProperty || new Party({ contact: new Contact() });
      updatedFields[ 'brand' ] = formModel.eventProperty.brand;
      updatedFields[ 'chain' ] = formModel.eventProperty.chain;
      updatedFields[ 'propertyId' ] = formModel.eventProperty.propertyId;
      updatedFields[ 'eventProperty' ] = Object.assign({}, eventProperty, model[ 'eventProperty' ]);
      updatedFields[ 'eventProperty' ][ 'contact' ] = Object.assign({}, eventProperty.contact, model[ 'eventProperty' ][ 'contact' ]);
    }

    updatedFields[ 'startDate' ] = new Date(formModel.startDate).toISOString();
    updatedFields[ 'endDate' ] = new Date(formModel.endDate).toISOString();

    const updatedModel = new OnyxEvent(Object.assign({}, formModel, updatedFields));
    updatedModel.eventProperty.eventReferenceNumber = formModel.groupCodeNumber;
    const returnedEvent = this.eventStore.updateEvent(updatedModel);

    returnedEvent.subscribe(
      () => {
        this.router.navigate(['/event', this.onyxEvent.eventId]);
      },
      error => {
        this.flashMessagesService.error('MESSAGES.ERROR.UPDATE_EVENT', error);
      });

    return returnedEvent;
  }

  edit(): void {
    this.router.navigate(['/event', this.onyxEvent.eventId, 'edit']);
  }


  confirmBeforeDeleting() {
    // set up the title of the confirmation modal
    const label = 'EVENT.DELETE_EVENT';
    this.confirmationModalConfig.title = this.translateService.instant(label);

    this.confirmationModal.open();
  }

  confirmBeforeDeletingHandler(value: any) {
    if (value) {
      const eventId = this.onyxEvent.eventId;
      this.delete();
    }
  }

  get canBeDeleted(): boolean {
    return this.onyxEvent.canBeDeleted;
  }

  delete(): void {
    this.eventStore.deleteEvent(this.onyxEvent.eventId).subscribe(() => {
      this.eventSearchParametersStore.updateUrl();
    });

  }

  cancel(): void {
    this.router.navigate(['/event', this.onyxEvent.eventId]);
    this.eventStore.selectedEvent.subscribe(event => {
      this.onyxEvent = new OnyxEvent(event);
    });
  }

  togglePageToReadOnly(readOnly: boolean): void {
    this.menuStore.setPageTitle(readOnly ? 'EVENT.EVENT_INFO_TITLE' : 'EVENT.TITLE_EDIT');
    this.menuStore.setIsReadOnly(readOnly);
  }

  isEventValid(): boolean {
    return EventUtils.isEventValid(this.onyxEvent);
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/UAT-GroupPay/workspace/pioneer-web/src/app/event/event/event.component.ts